import type { StorageUsage } from "../api";
import { daysUntil, formatBytes } from "../format";
import { Link } from "../router";

const KINDS = [
  { key: "text", label: "Text" },
  { key: "pictures", label: "Pictures" },
  { key: "videos", label: "Videos" },
  { key: "files", label: "Files" },
  { key: "emails", label: "Original emails" },
] as const;

function percent(part: number, whole: number) {
  return whole > 0 ? Math.min(100, (part / whole) * 100) : 0;
}

function level(usage: StorageUsage) {
  const p = percent(usage.used, usage.limit);
  return p >= 100 ? "full" : p >= 90 ? "high" : "ok";
}

/** Sidebar: "200 MB of 1 GB" with a thin bar; links to the details in Settings. */
export function StorageMini({ usage, onClick }: { usage: StorageUsage; onClick?: () => void }) {
  return (
    <Link to="/settings#storage" className={`storage-mini storage-${level(usage)}`} onClick={onClick}>
      <span className="storage-mini-text">
        <span>Storage</span>
        <span>
          {formatBytes(usage.used)} / {formatBytes(usage.limit)}
        </span>
      </span>
      <span className="storage-bar" aria-hidden="true">
        <span style={{ width: `${Math.max(percent(usage.used, usage.limit), usage.used > 0 ? 1 : 0)}%` }} />
      </span>
      {packNotice(usage) && <span className="storage-notice">{packNotice(usage)}</span>}
    </Link>
  );
}

/** "Storage pack ends in 12 days" within 30 days of the end; "ended" if it lapsed while you're over the limit. */
function packNotice(usage: StorageUsage): string | null {
  const soonest = usage.packs.filter((p) => p.active).map((p) => daysUntil(p.expiresAt)).sort((a, b) => a - b)[0];
  if (soonest !== undefined && soonest <= 30) return `Storage pack ends in ${soonest} ${soonest === 1 ? "day" : "days"}: renew`;
  if (usage.used >= usage.limit && usage.packs.some((p) => !p.active)) return "Storage pack ended: renew to save again";
  return null;
}

/** Settings: the bar split by kind, with a legend. */
export function StorageDetails({ usage }: { usage: StorageUsage }) {
  return (
    <div className={`storage-details storage-${level(usage)}`}>
      <p className="storage-total">
        <strong>{formatBytes(usage.used)}</strong> of {formatBytes(usage.limit)} used
        <span className="muted"> · {Math.round(percent(usage.used, usage.limit))}%</span>
      </p>
      <div className="storage-bar storage-bar-split" role="img" aria-label={`${formatBytes(usage.used)} of ${formatBytes(usage.limit)} used`}>
        {KINDS.map(({ key }) => (
          <span key={key} className={`kind-${key}`} style={{ width: `${percent(usage.breakdown[key], usage.limit)}%` }} />
        ))}
      </div>
      <ul className="storage-legend">
        {KINDS.filter(({ key }) => key !== "emails" || usage.breakdown.emails > 0).map(({ key, label }) => (
          <li key={key}>
            <span className={`dot kind-${key}`} aria-hidden="true" />
            {label}
            <span className="muted">{formatBytes(usage.breakdown[key])}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
